import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export function useHostData() {
  const [summary, setSummary] = useState(null);
  const [earnings, setEarnings] = useState({ ready: 0, pending: 0, paid: 0, rows: [] });
  const [listings, setListings] = useState([]);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [connect, setConnect] = useState(null);

  const loadConnect = useCallback(() => {
    api.get("/host/me/connect/status").then((r) => setConnect(r.data)).catch(() => setConnect(null));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/host/me/summary").then((r) => setSummary(r.data)).catch((e) => {
      if (e.response?.status === 403) setDenied(true);
    });
    loadConnect();
    Promise.all([
      api.get("/host/me/earnings").then((r) => setEarnings(r.data)).catch(() => {}),
      api.get("/host/me/listings").then((r) => setListings(r.data)).catch(() => {}),
      api.get("/host/me/trips").then((r) => setTrips(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [loadConnect]);

  useEffect(() => { load(); }, [load]);

  const connectBank = async () => {
    try {
      const r = await api.post("/host/me/connect/onboard", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openBankDashboard = async () => {
    try {
      const r = await api.post("/host/me/connect/dashboard");
      window.open(r.data.url, "_blank");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addBlock = async (carId, start, end, reason, onDone) => {
    try {
      await api.post(`/host/me/cars/${carId}/blocks`, { start, end, reason });
      toast.success("Dates blocked");
      onDone?.();
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeBlock = async (carId, blockId) => {
    try {
      await api.delete(`/host/me/cars/${carId}/blocks/${blockId}`);
      toast.success("Block removed");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const editListing = async (carId, payload, onDone) => {
    try {
      await api.put(`/host/me/cars/${carId}`, payload);
      toast.success("Listing updated — it's back under review for approval.");
      onDone?.();
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setListingAvailability = async (carId, available) => {
    try {
      await api.put(`/host/me/cars/${carId}/availability`, { available });
      toast.success(available ? "Listing resumed" : "Listing paused");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteListing = async (carId, onDone) => {
    try {
      await api.delete(`/host/me/cars/${carId}`);
      toast.success("Listing removed");
      onDone?.();
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return {
    summary, earnings, listings, trips, loading, denied, load,
    addBlock, removeBlock, editListing, setListingAvailability, deleteListing,
    connect, loadConnect, connectBank, openBankDashboard,
  };
}
